import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import styles from "./ResetPasswordComponent.module.css";
import PasswordValidator from "../../utils/PasswordValidator/PasswordValidator";
import {resetPassword} from "../../api/AuthApi";

const ResetPasswordComponent: React.FC = () => {
    const {token} = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const [passwordError, setPasswordError] = useState(false);
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (success) {
            timer = setTimeout(() => {
                navigate("/login");
            }, 10000);
        }
        return () => clearTimeout(timer);
    }, [success, navigate]);

    const isPasswordValid = () => {
        const passwordValidationRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
        return passwordValidationRegex.test(password);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        setMessage(null);
        setPasswordError(false);
        setConfirmPasswordError(false);

        if (!isPasswordValid()) {
            setMessage("The new password does not meet the requirements.");
            setPasswordError(true);
            return;
        }

        if (password !== confirmPassword) {
            setMessage("Passwords do not match!");
            setConfirmPasswordError(true);
            return;
        }

        if (!token) {
            setMessage("Invalid or missing reset token.");
            return;
        }

        try {
            await resetPassword({token, password});
            setSuccess(true);
            setMessage(
                "Password reset successful. You will be redirected to the login page in 10 seconds, or you can click the link below."
            );
        } catch (error: any) {
            setMessage(error.message ?? "An error occurred. Please try again.");
        }
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Reset Password</h1>
            {!success ? (
                <>
                    <input
                        className={`${styles.input} ${passwordError ? styles.inputError : ""}`}
                        type="password"
                        placeholder="New Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <PasswordValidator password={password}/>
                    {passwordError && <p className={styles.error}>The new password is invalid.</p>}

                    <input
                        className={`${styles.input} ${confirmPasswordError ? styles.inputError : ""}`}
                        type="password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    {confirmPasswordError && <p className={styles.error}>Passwords must match.</p>}

                    <button className={styles.button} onClick={handleSubmit}>
                        Reset Password
                    </button>

                    {message && !passwordError && !confirmPasswordError && (
                        <p className={styles.error}>{message}</p>
                    )}
                </>
            ) : (
                <>
                    <p className={styles.success}>{message}</p>
                    <p>
                        <a href="/login" onClick={() => navigate("/login")}>
                            Go to login page now
                        </a>
                    </p>
                </>
            )}
        </div>
    );
};

export default ResetPasswordComponent;